'use client'

import type React from 'react'

import {
  ChartContentContainer,
  ChartLoadingStateContainer,
  ChartTransitionContainer,
} from './styles'

interface ChartTransitionWrapperProps {
  isLoading?: boolean
  loadingContent: React.ReactNode
  children: React.ReactNode
  height?: string
  className?: string
}

/**
 * A reusable wrapper component that provides smooth loading transitions for charts.
 *
 * This component handles the transition between loading and loaded states using
 * Framer Motion animations.
 *
 * @example
 * ```tsx
 * <ChartTransitionWrapper
 *   isLoading={isDataLoading}
 *   height="h-[246px]"
 *   loadingContent={
 *     <BarChartSkeleton
 *       height={246}
 *       barCount={10}
 *       waveType="trending"
 *     />
 *   }
 * >
 *   <MultipleBarChart
 *     data={chartData}
 *     xAxisKey="month"
 *     bars={bars}
 *   />
 * </ChartTransitionWrapper>
 * ```
 *
 * @param props - The component props
 * @returns A chart wrapper with loading transition animations
 */

// biome-ignore lint/style/noDefaultExport: Existing default export — prefer named exports for new code
export default function ChartTransitionWrapper({
  isLoading,
  loadingContent,
  children,
  height = 'h-auto',
  className,
}: ChartTransitionWrapperProps) {
  return (
    <ChartTransitionContainer height={height} className={className}>
      <ChartLoadingStateContainer isLoading={isLoading} height={height}>
        {loadingContent}
      </ChartLoadingStateContainer>
      <ChartContentContainer isLoading={isLoading} height={height}>
        {children}
      </ChartContentContainer>
    </ChartTransitionContainer>
  )
}
